import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Toaster, type Toast } from '@forecastify/ui';
import { useT } from '../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The confirmation every write in the redesign ends with.                   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **`ui`'s `Toaster`, with the console's own small queue in front of it.** The mockup confirms
 * every verb with a toast — *Meghívó elküldve · nev@ceg.hu* — where the first version of this
 * console printed a green line somewhere on the page, in a different place on each screen.
 *
 * ⚠️ **Gone after five seconds**, and dismissable before that — the `Toaster`'s own timer, which
 * this queue used to run itself. A confirmation that stays is a notification, and this console
 * deliberately has none.
 */

type Push = (toast: Omit<Toast, 'id'>) => void;

const ToastContext = createContext<Push | undefined>(undefined);

export function ToastProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const t = useT();
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string | number): void => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback<Push>((toast) => {
    const id = `${String(Date.now())}-${String(Math.random())}`;
    setToasts((current) => [...current, { variant: 'success', ...toast, id }]);
  }, []);

  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster
        toasts={toasts}
        onDismiss={dismiss}
        label={t('toast.region')}
        dismissLabel={t('toast.dismiss')}
      />
    </ToastContext.Provider>
  );
}

/** ⚠️ A no-op outside the provider, so a screen rendered alone in a test does not throw. */
export function useToast(): Push {
  return useContext(ToastContext) ?? noop;
}

function noop(): void {
  /* No provider: nothing to show the toast in. */
}
